// src/components/Steps/ChargeSetup/ChargeSetup.jsx
import React, { useState, useEffect, useRef } from 'react';
import { track } from '../../../telemetry/journey.js';
import { useNavigate } from 'react-router-dom';
import { LuArrowLeft } from 'react-icons/lu';
import './ChargeSetup.less';
import { ActionButtonWrapper, EvHeader, Notice } from '../../index.js';
import useElementHeight from '../../../hooks/useElementHeight.js';
import { PAYMENTS_ENABLED, ACCOUNT_CHOICE_ENABLED } from '../../../config/features.js';
import { ACCOUNT_CHOICE } from '../../../constants/accountChoice.js';
import { checkRegisteredPlate, normalizePlateDisplay } from '../../../api/vehicles.js';
import { useSession } from '../../../context/SessionContext.jsx';
import { useAuth } from '../../../context/AuthContext.jsx';
import { useUserVehicles } from '../../../hooks/useUserVehicles.js';
import { useToast } from '../../../context/ToastContext.jsx';
import { createCustomerSession, normalizeApiError } from '../../../api/sessionsApi.js';
import { clearBlockingSession } from '../../../utils/cancelSession.js';
import { useCancelAttempt } from '../../../hooks/useCancelAttempt.jsx';
import { STEPS } from '../../../constants/steps.js';
import { isAccessOutcome } from '../../../utils/accessOutcome.js';
import { onVisibilityChange as onKeyboardChange } from '../../../platform/keyboard.js';
import { resolveVehicleChargingRules } from '../../../utils/billingRules.js';
import SavedVehiclePicker from './SavedVehiclePicker.jsx';

// Scroll `scroller` just enough that `target` ends above whatever is visible
// at the bottom: the scroller's own edge, or the top of an on-screen keyboard
// (visualViewport). Never so far that `keepVisible` (the plate field) leaves
// the top: with both on screen the driver can read the message and fix the
// plate without dismissing anything.
const REVEAL_GAP = 16;
function revealAboveKeyboard(scroller, target, keepVisible) {
  if (!scroller || !target || typeof scroller.scrollBy !== 'function') return;
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const box = scroller.getBoundingClientRect();
  const visibleTop = Math.max(box.top, vv ? vv.offsetTop : 0);
  const visibleBottom = Math.min(box.bottom, vv ? vv.offsetTop + vv.height : window.innerHeight);
  const overflow = target.getBoundingClientRect().bottom + REVEAL_GAP - visibleBottom;
  if (overflow <= 0) return;
  const room = keepVisible
    ? Math.max(0, keepVisible.getBoundingClientRect().top - REVEAL_GAP / 2 - visibleTop)
    : overflow;
  const by = Math.min(overflow, room);
  if (by > 0) scroller.scrollBy({ top: by, behavior: 'smooth' });
}

const ChargeSetup = ({ handleIncreaseStep, handleDecreaseStep }) => {
  const { ref, height } = useElementHeight();
  const navigate = useNavigate();
  const { state, update, rehydrateSession, setStationData, setStep, prepareForNewSession } = useSession();
  // Back from here leaves the journey: the shared single-flight cancel.
  const { cancelNow } = useCancelAttempt();
  const { user, isBooting: isAuthBooting } = useAuth();
  const { vehicles: savedVehicles, isLoading: isLoadingVehicles } = useUserVehicles();
  const { showToast } = useToast();
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [connectorBusyError, setConnectorBusyError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [licensePlate, setLicensePlate] = useState(state.vehicle?.licensePlateRaw || '');
  const [retryAttempts, setRetryAttempts] = useState(0);

  // "Use another vehicle" opt-out — persists only within this session
  // render. `state.selectedVehicleId` is the durable source of truth
  // for whether a saved vehicle is in play.
  const [useAnotherVehicle, setUseAnotherVehicle] = useState(false);
  // Remembers the previously-selected saved vehicle so the reversible
  // "Back to saved vehicles" action can restore the exact selection
  // the user came from, rather than falling back to a default.
  const previousSelectedIdRef = useRef(null);

  // Redeem/credit code is applied one step earlier on the Pre-Auth
  // Hold screen (AmountPicker). Read it from session context so
  // createCustomerSession can attach it to the request without the
  // user having to re-enter anything on this screen.
  const appliedCredit = state.appliedCredit || null;
  const pollingTimeoutRef = React.useRef(null);
  const pollingIntervalRef = React.useRef(null);
  const MAX_RETRY_ATTEMPTS = 3; // Maximum retry attempts to prevent endless loops

  // Cleanup polling timer on unmount
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, []);

  // ── Fresh-flow payment-safety boundary ─────────────────────────
  // Genuinely-new charging flow = ChargeSetup mounted with no active
  // sessionUid (session has NOT been created yet AND active-session
  // rehydration is not in play — rehydration always populates
  // sessionUid before this component can mount fresh). Clear the
  // stale payment-decision and vehicle-trust markers exactly once so
  // a paid session cannot inherit "verified + paymentRequired:false"
  // from a prior FLEET/FREE flow. Guarded by a ref so back-navigation
  // from AMOUNT_PICKER / MAKE_PAYMENT does not re-fire (sessionUid is
  // set at that point anyway).
  const freshResetFiredRef = useRef(false);
  useEffect(() => {
    if (freshResetFiredRef.current) return;
    if (state.sessionUid) return;
    freshResetFiredRef.current = true;
    prepareForNewSession();
    // Empty deps on purpose: this is a one-shot mount-time boundary.
    // The sessionUid check above uses the value captured at first
    // render; if a session gets created later on this same mount,
    // the guard has already fired and won't re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Sync local licensePlate state with session state
  // This ensures the input is populated when returning from PIN verification
  useEffect(() => {
    const storedPlate = state.licensePlate || state.vehicle?.licensePlateRaw || '';
    if (storedPlate && storedPlate !== licensePlate) {
      console.log('[ChargeSetup] Syncing license plate from session state:', storedPlate);
      setLicensePlate(storedPlate);
    }
  }, [state.licensePlate, state.vehicle?.licensePlateRaw]);
  
  // ── Registered path ("I am registered") ────────────────────────
  // The driver told us their vehicle is registered, so the plate is
  // required and is checked before anything is created: a recognised plate
  // goes on to the existing session + PIN flow, an unrecognised one stays
  // here. Checking authorises nothing -- plate + PIN verification still does.
  // Flag OFF, guests and a picked saved vehicle keep the optional plate.
  // 'idle' | 'invalid' | 'checking' | 'not_recognised' | 'unavailable'
  const [plateCheck, setPlateCheck] = useState('idle');
  // Held for a whole submit attempt (lookup + session creation); see submitContinue.
  const continueInFlight = useRef(false);
  const plateInputRef = useRef(null);
  // The answer this screen was opened with. prepareForNewSession() -- run
  // on mount when no session exists yet, i.e. right after "I am registered"
  // -- resets accountChoice so a previous driver's answer is never reused;
  // this screen must still honour the answer that brought the driver here.
  const [openedAsRegistered] = useState(() => state.accountChoice === ACCOUNT_CHOICE.ACCOUNT);
  // The plate messages sit below the field; on a phone they can land under
  // the fold of the scrolling body -- or, with the keyboard up, under the
  // keyboard, which the page's layout knows nothing about. The OS keeps the
  // FOCUSED FIELD above its keyboard; nothing does that for our message.
  // So: while a keyboard is up, pad the scrolling body by its height (room
  // to scroll into), and scroll just enough to lift the message (or the
  // field) above the visible bottom. If the OS already panned it into view
  // this measures nothing to do. The keyboard itself is left alone.
  const plateCheckRef = useRef(null);
  const bodyRef = useRef(null);
  const [keyboardInset, setKeyboardInset] = useState(0);
  useEffect(() => onKeyboardChange(({ visible, height }) => setKeyboardInset(visible ? height : 0)), []);
  useEffect(() => {
    const showing = plateCheck === 'invalid' || plateCheck === 'not_recognised' || plateCheck === 'unavailable';
    const fieldFocused = document.activeElement === plateInputRef.current;
    if (!showing && !(keyboardInset && fieldFocused)) return;
    revealAboveKeyboard(bodyRef.current, showing ? plateCheckRef.current : plateInputRef.current, plateInputRef.current);
  }, [plateCheck, keyboardInset]);

  // Handle license plate input change. Letters are uppercased in the VALUE
  // itself (typing and pasting both arrive here), not just displayed so;
  // every other character is left for the existing validation to judge.
  const handleLicensePlateChange = (e) => {
    const el = e.target;
    const value = String(el.value ?? '').toUpperCase();
    if (value !== el.value) {
      // Rewrite the field in place, in this same event, keeping the caret
      // where the driver left it. Deferring the caret fix let a fast next
      // keystroke land at a stale position and scramble the plate.
      const { selectionStart, selectionEnd } = el;
      el.value = value;
      try { el.setSelectionRange(selectionStart, selectionEnd); } catch { /* not a text input */ }
    }
    setPlateCheck('idle');
    setLicensePlate(value);
    // Store in session context vehicle state
    update({
      licensePlate: value, // Store in both locations for consistency
      vehicle: {
        ...state.vehicle,
        licensePlateRaw: value,
        verified: false  // Reset verification when plate changes
      }
    });
  };

  // "Continue as guest" from an unrecognised plate: the existing guest
  // flow (as from the first screen) -- the typed plate is dropped and the
  // guest goes to the budget screen, where its session is created.
  const continueAsGuest = () => {
    setPlateCheck('idle');
    setLicensePlate('');
    update({
      accountChoice: ACCOUNT_CHOICE.GUEST,
      licensePlate: null,
      vehicle: { ...state.vehicle, licensePlateRaw: null, verified: false },
    });
    setStep(STEPS.AMOUNT_PICKER);
  };

  // ── Saved-vehicle selection ─────────────────────────────────────
  // When the user is signed in and has vehicles registered we show
  // the "Choose a vehicle" section above the manual plate input.
  // Signed-out users, users with zero vehicles, and users who opt
  // into "Use another vehicle" continue to see the existing manual
  // plate flow with no behavioural change.
  const isAuthenticated = !!user?.id;
  const savedVehicleCount = Array.isArray(savedVehicles) ? savedVehicles.length : 0;
  const savedVehiclePickerVisible =
    !isAuthBooting &&
    !isLoadingVehicles &&
    isAuthenticated &&
    savedVehicleCount > 0 &&
    !useAnotherVehicle;

  // Returning-vehicle recognition deliberately does NOT run here.
  //
  // A recognised licence plate must never become an authentication
  // requirement for starting a charge. The conversion prompt added in
  // #33 offered "Continue with GreenCharge", which routed the driver to
  // /auth -> email -> OTP (-> onboarding, for the guest_provisional user
  // a receipt email creates). The card was dismissible, but it put an
  // account funnel directly on the charging path and read as a gate.
  //
  // Plate recognition may still HELP the driver -- billing rules come
  // from the authoritative /vehicles/verify PIN response -- but it must
  // never add steps, ask for an email, or unlock anything belonging to
  // an account. If account continuation returns, it belongs AFTER the
  // charge has started, never between the plate and the charge.

  // Prefill session state from a saved-vehicle card. Ownership was
  // proven by the JWT that returned this vehicle from /api/vehicles/mine,
  // but PIN possession is still required — verified stays FALSE here.
  // The PIN step (VEHICLE_VERIFICATION) is what flips `verified` after
  // the plate + PIN call succeeds.
  //
  // `advance` gates the navigation side effect so an auto-preselect for
  // single-vehicle accounts does not yank the user off ChargeSetup on
  // mount. User-initiated clicks pass `advance: true`.
  const selectSavedVehicle = React.useCallback((vehicle, { advance = true } = {}) => {
    if (!vehicle || !vehicle.id) return;
    const plateRaw = vehicle.licensePlate || vehicle.plateNormalized || '';
    const plateNorm = vehicle.plateNormalized || vehicle.licensePlate || '';
    update({
      selectedVehicleId: vehicle.id,
      // Non-secret display metadata for the PIN step subtitle. Never
      // stores billing rules speculatively — those come from the
      // authoritative /vehicles/verify response.
      selectedVehicle: {
        id: vehicle.id,
        displayName: vehicle.displayName || null,
        licensePlate: plateRaw || null,
        plateNormalized: plateNorm || null,
      },
      // Clear the legacy top-level plate so it doesn't override the
      // saved vehicle in downstream reads.
      licensePlate: plateRaw,
      vehicle: {
        ...state.vehicle,
        vehicleId: vehicle.id,
        licensePlateRaw: plateRaw || null,
        licensePlateNormalized: plateNorm || null,
        // Verified only after the PIN succeeds. Billing rules and
        // paymentRequired are left untouched — the PIN response
        // sets them authoritatively so we never bypass card entry
        // on a speculative saved-vehicle click.
        verified: false,
      },
    });
    setLicensePlate('');
    setUseAnotherVehicle(false);

    if (advance) {
      // Direct navigation to the PIN sub-step. Session is not created
      // yet — the PIN endpoint is plate + PIN only. Downstream steps
      // create/upsert the session when they need it. Back from PIN
      // returns to CHARGE_SETUP (STEP_BACK[2.5]=2) with the saved
      // vehicle still highlighted because `selectedVehicleId` and
      // `selectedVehicle` remain in session state.
      setStep(STEPS.VEHICLE_VERIFICATION);
    }
  }, [update, setStep, state.vehicle]);

  const useAnotherVehicleFlow = React.useCallback(() => {
    // Stash the current saved-vehicle selection so a subsequent
    // "Back to saved vehicles" can restore it exactly. `null` is a
    // valid stash value — it means the user was in the picker but
    // had not chosen anything yet.
    previousSelectedIdRef.current = state.selectedVehicleId || null;
    setUseAnotherVehicle(true);
    setLicensePlate('');
    // Clear the saved-vehicle selection so the manual plate + PIN
    // path is used again from a clean slate. selectedVehicle display
    // metadata is cleared alongside the id so the PIN step never
    // renders stale name/plate text.
    update({
      selectedVehicleId: null,
      selectedVehicle: null,
      licensePlate: '',
      vehicle: {
        ...state.vehicle,
        vehicleId: null,
        licensePlateRaw: null,
        licensePlateNormalized: null,
        verified: false,
        billingMode: null,
        rateOverride: null,
        paymentRequired: true,
        rateApplied: null,
      },
    });
  }, [update, state.vehicle, state.selectedVehicleId]);

  // Reversible mode switch: return from manual mode to the saved
  // selector without navigating away or submitting anything. Clears
  // the in-flight plate input and restores the previously-selected
  // vehicle when there was one.
  const backToSavedVehiclesFlow = React.useCallback(() => {
    setUseAnotherVehicle(false);
    setLicensePlate('');
    const priorId = previousSelectedIdRef.current;
    const prior = priorId
      ? savedVehicles.find((v) => v && v.id === priorId)
      : null;
    if (prior) {
      // Restore the saved-vehicle selection WITHOUT re-navigating to
      // the PIN step. The user asked to return to the picker; jumping
      // straight to PIN would be surprising and undo the intent.
      selectSavedVehicle(prior, { advance: false });
      return;
    }
    // No prior selection to restore. Clear any manual-plate residue
    // that landed on session state so downstream steps don't see a
    // ghost plate.
    update({
      licensePlate: '',
      vehicle: {
        ...state.vehicle,
        licensePlateRaw: null,
        licensePlateNormalized: null,
        verified: false,
      },
    });
  }, [savedVehicles, selectSavedVehicle, update, state.vehicle]);

  // Auto-preselect was previously fired for accounts that own exactly
  // one vehicle. It is now DISALLOWED: selection must be optional per
  // the "Continue to Payment" contract. An auto-preselect writes
  // `state.licensePlate = <plate>`, and the Continue handler's plate
  // check would then force the user through PIN verification even
  // though they never clicked — silently blocking every single-vehicle
  // account from continuing without PIN. Users must be able to press
  // the CTA and continue into the normal paid flow WITHOUT selecting
  // a saved vehicle at all. If they DO want to use a saved vehicle,
  // they click the card (which advances straight to PIN via
  // selectSavedVehicle).

  // Rehydrate session if sessionUid exists on mount
  useEffect(() => {
    const tryRehydrate = async () => {
      if (state.sessionUid) {
        console.log('[ChargeSetup] Found existing sessionUid, attempting rehydration');
        const result = await rehydrateSession();
        
        if (!result.success) {
          console.log('[ChargeSetup] Rehydration failed, session cleared');
          // Session was cleared by rehydrateSession
        } else {
          console.log('[ChargeSetup] Rehydration successful, session is valid');
        }
      }
    };
    
    tryRehydrate();
  }, []); // Only run on mount
  
  // Helper: Re-fetch station status
  const refetchStationStatus = async () => {
    if (!state.stationId) return null;
    
    try {
      console.log('[ChargeSetup] Re-fetching station status...');
      const { fetchStationDataById } = await import('../../../api/fetch.js');
      const deviceData = await fetchStationDataById(state.stationId);
      
      if (deviceData) {
        setStationData(deviceData);
        const status = deviceData?.station_status?.[0]?.status;
        console.log('[ChargeSetup] Station status refreshed:', status);
        return status;
      }
    } catch (error) {
      console.error('[ChargeSetup] Failed to refresh station status:', error);
    }
    return null;
  };
  
  // Helper: Start polling for status recovery WITH AUTO-RETRY
  const startStatusPolling = async () => {
    console.log('[ChargeSetup] Starting status polling for recovery...');
    setIsPolling(true);
    setConnectorBusyError({
      message: 'Connector is currently in use. Checking for availability...',
      canRetry: false
    });
    
    let pollCount = 0;
    const maxPolls = 5; // 5 polls = 15 seconds
    
    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      console.log(`[ChargeSetup] Polling attempt ${pollCount}/${maxPolls}`);
      
      const status = await refetchStationStatus();
      
      if (status === 'Available') {
        console.log('[ChargeSetup] ✅ Status recovered to Available - auto-retrying session creation');
        clearInterval(pollingIntervalRef.current);
        setIsPolling(false);
        setConnectorBusyError(null);
        
        // Check retry limit
        if (retryAttempts >= MAX_RETRY_ATTEMPTS) {
          console.warn(`[ChargeSetup] Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Manual retry required.`);
          setConnectorBusyError({
            message: 'Multiple attempts failed. Please wait a moment and try again manually.',
            canRetry: true
          });
          showToast({
            message: 'Please wait a moment before trying again.',
            type: 'warning'
          });
          return;
        }
        
        // Increment retry counter
        setRetryAttempts(prev => prev + 1);
        console.log(`[ChargeSetup] Auto-retry attempt ${retryAttempts + 1}/${MAX_RETRY_ATTEMPTS}`);
        
        // AUTO-RETRY: Attempt to create session again now that connector is Available
        try {
          const pricingSnapshot = {
            rate_per_kwh: state.stationMeta.ratePerKwh,
            rate_per_minute: state.stationMeta.ratePerMinute || null,
            billing_unit: state.stationMeta.billingUnit || 'kwh',
            session_fee: state.stationMeta.sessionFee || 0,
            currency: state.currency || 'BBD',
            min_charge_amount: state.stationMeta.minChargeAmount || 0,
          };
          
          console.log('[ChargeSetup] Auto-retry: Creating session after recovery...');
          // Auto-retry after CONNECTOR_BUSY: the first attempt's tx
          // rolled back so the code is still 'active' and safe to
          // reapply. Include it so the customer doesn't get charged
          // full-amount just because the initial start needed a poll.
          // Plate attribution is carried through the retry too — losing
          // it silently on a recovery-path would be a subtle regression.
          const _retryVehicleId = state.selectedVehicleId || state.vehicle?.vehicleId || null;
          const _retryTypedPlate = state.vehicle?.licensePlateRaw || state.licensePlate || null;
          const retryResult = await createCustomerSession({
            stationId: state.stationId,
            // connectorId is REQUIRED and must be a real physical
            // connector id selected by the driver (or auto-selected
            // for single-connector chargers) in EvProfile. No fallback
            // to 1 — see createCustomerSession's INVALID_CONNECTOR_ID
            // guard for the failure mode when this is null.
            connectorId: state.connectorId,
            pricingSnapshot,
            redeemCode: appliedCredit ? appliedCredit.code : null,
            vehicleId: _retryVehicleId,
            licensePlate: _retryVehicleId ? null : _retryTypedPlate,
            plateCaptureSource: _retryVehicleId
              ? 'saved_vehicle'
              : (_retryTypedPlate ? 'manual_entry' : null),
          });
          
          if (retryResult.sessionUid) {
            console.log('[ChargeSetup] ✅ Auto-retry successful! Session created:', retryResult.sessionUid);
            localStorage.setItem('activeSessionUid', retryResult.sessionUid);
            
            // Reset retry counter on success
            setRetryAttempts(0);
            
            // Calculate session budget
            let sessionBudgetCents;
            if (state.authorizedAmount && state.authorizedAmount > 0) {
              sessionBudgetCents = state.authorizedAmount;
            } else {
              const minChargeAmountCents = Math.round((pricingSnapshot.min_charge_amount || 0) * 100);
              sessionBudgetCents = minChargeAmountCents > 0 ? minChargeAmountCents : 1000;
            }
            
            update({
              sessionUid: retryResult.sessionUid,
              sessionId: retryResult.sessionUid,
              sessionStatus: retryResult.session?.status,
              // Server tariff when the API applied one (P4b), else the snapshot sent.
              pricingSnapshot: retryResult.pricingSnapshot || pricingSnapshot,
              authorization: retryResult.authorization ?? null,
              sessionBudgetCents: sessionBudgetCents,
            });
            
            showToast({
              message: 'Charger is now available. Proceeding...',
              type: 'success',
              duration: 2000
            });
            
            // Proceed to next step after brief delay
            setTimeout(() => {
              handleIncreaseStep();
            }, 500);
          } else {
            // Retry failed - show error
            console.error('[ChargeSetup] Auto-retry failed:', retryResult.error);
            setConnectorBusyError({
              message: retryResult.message || 'Connector still unavailable. Please try again.',
              canRetry: true
            });
            showToast({
              message: retryResult.message || 'Still unable to connect. Please try again.',
              type: 'error'
            });
          }
        } catch (retryError) {
          console.error('[ChargeSetup] Auto-retry exception:', retryError);
          setConnectorBusyError({
            message: 'Failed to create session. Please try again.',
            canRetry: true
          });
          showToast({
            message: 'Failed to create session. Please try again.',
            type: 'error'
          });
        }
        
        return;
      }
      
      if (pollCount >= maxPolls) {
        console.log('[ChargeSetup] Polling timeout reached');
        clearInterval(pollingIntervalRef.current);
        setIsPolling(false);
        setConnectorBusyError({
          message: 'Connector is currently in use. Try again in a moment.',
          canRetry: true
        });
      }
    }, 3000); // Poll every 3 seconds
    
    // Set timeout to stop polling after 15s
    pollingTimeoutRef.current = setTimeout(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        setIsPolling(false);
      }
    }, 15000);
  };
  
  // Helper: Handle CONNECTOR_BUSY with blocking session clear
  const handleConnectorBusy = async (initialResult) => {
    console.log('[ChargeSetup] CONNECTOR_BUSY detected, checking for blocking session...');
    
    // CRITICAL: Check if backend returned blocking session UID
    const blockingSessionUid = initialResult.blocking?.session_uid;
    
    if (blockingSessionUid) {
      console.log('[ChargeSetup] Backend provided blocking session UID:', blockingSessionUid);
      
      // Show "Clear previous attempt" UI
      setConnectorBusyError({
        message: 'This connector has an active session.',
        canClear: true,
        blockingSessionUid,
        canRetry: false
      });
      
      return { success: false, hasBlockingSession: true, blockingSessionUid };
    }
    
    // No blocking session UID - fall back to auto-recovery
    console.log('[ChargeSetup] No blocking session UID, attempting auto-recovery...');
    
    // Step 1: Clear any stale local sessionUid if not actively charging
    if (state.sessionUid) {
      const isActivelyCharging = state.live?.status === 'Charging' || 
                                 state.live?.status === 'Preparing' ||
                                 state.live?.tx_id;
      
      if (!isActivelyCharging) {
        console.log('[ChargeSetup] Clearing stale sessionUid...');
        localStorage.removeItem('activeSessionUid');
        update({ sessionUid: null, sessionId: null });
      }
    }
    
    // Step 2: Re-fetch station status
    await refetchStationStatus();
    
    // Step 3: Wait 1.5s and retry ONCE
    console.log('[ChargeSetup] Waiting 1.5s before retry...');
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    console.log('[ChargeSetup] Retrying session creation (attempt 2)...');
    
    const pricingSnapshot = {
      rate_per_kwh: state.stationMeta.ratePerKwh,
      rate_per_minute: state.stationMeta.ratePerMinute || null,
      billing_unit: state.stationMeta.billingUnit || 'kwh',
      session_fee: state.stationMeta.sessionFee || 0,
      currency: state.currency || 'BBD',
      min_charge_amount: state.stationMeta.minChargeAmount || 0,
    };
    
    // Same plate-attribution passthrough as the primary path — a
    // user-initiated manual retry should not lose the driver's saved-
    // vehicle pick.
    const _retryVehicleId = state.selectedVehicleId || state.vehicle?.vehicleId || null;
    const _retryTypedPlate = state.vehicle?.licensePlateRaw || state.licensePlate || null;
    const retryResult = await createCustomerSession({
      stationId: state.stationId,
      // See Phase 1b remediation: never fall back to connector 1
      // on a multi-connector charger. The EvProfile picker records
      // the real physical connector; if it's null here that's a
      // legitimate error state, not a "pick connector 1 for them"
      // scenario.
      connectorId: state.connectorId,
      pricingSnapshot,
      redeemCode: appliedCredit ? appliedCredit.code : null,
      vehicleId: _retryVehicleId,
      licensePlate: _retryVehicleId ? null : _retryTypedPlate,
      plateCaptureSource: _retryVehicleId
        ? 'saved_vehicle'
        : (_retryTypedPlate ? 'manual_entry' : null),
    });

    if (retryResult.sessionUid) {
      console.log('[ChargeSetup] Retry successful!');
      localStorage.setItem('activeSessionUid', retryResult.sessionUid);
      update({
        sessionUid: retryResult.sessionUid,
        sessionId: retryResult.sessionUid,
        sessionStatus: retryResult.session?.status,
        // Server tariff when the API applied one (P4b), else the snapshot sent.
        pricingSnapshot: retryResult.pricingSnapshot || pricingSnapshot,
        authorization: retryResult.authorization ?? null,
      });
      return { success: true };
    }
    
    // Step 4: If retry still fails, start polling
    if (retryResult.error === 'CONNECTOR_BUSY') {
      console.log('[ChargeSetup] Retry failed, starting status polling...');
      startStatusPolling();
      return { success: false, startedPolling: true };
    }
    
    // Other error
    return { success: false, error: retryResult.message || retryResult.error };
  };
  
  // Handle clear blocking session button click
  const handleClearBlockingSession = async () => {
    if (!connectorBusyError?.blockingSessionUid) return;
    
    console.log('[ChargeSetup] Clearing blocking session:', connectorBusyError.blockingSessionUid);
    
    setConnectorBusyError({
      ...connectorBusyError,
      message: 'Clearing previous session...',
      canClear: false
    });
    
    const result = await clearBlockingSession(
      connectorBusyError.blockingSessionUid,
      {
        stationId: state.stationId,
        connectorId: state.connectorId
      }
    );
    
    if (result.success) {
      console.log('[ChargeSetup] Blocking session cleared successfully');
      showToast({
        message: 'Previous session cleared. Retrying...',
        type: 'success'
      });
      
      // Clear the error UI
      setConnectorBusyError(null);
      
      // Wait briefly then retry create session
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Trigger create session again
      handleActionClick('continue');
    } else {
      console.error('[ChargeSetup] Failed to clear blocking session:', result.error);
      showToast({
        message: result.message || 'Failed to clear previous session',
        type: 'error'
      });
      
      setConnectorBusyError({
        message: 'Failed to clear previous session. Please try again.',
        canRetry: true,
        canClear: false
      });
    }
  };

  // Conditional content based on payments feature flag
  const headerLabel = PAYMENTS_ENABLED ? 'Confirm & Pay' : 'Confirm Session';
  const heading = PAYMENTS_ENABLED 
    ? 'Ready to confirm and pay for your session?'
    : 'Ready to start your charging session?';
  const bodyText = PAYMENTS_ENABLED
    ? 'Continue to review pricing and complete payment to start your charging session.'
    : "When you're ready, tap continue to plug in and begin your charging session.";
  // Context-aware CTA label:
  //   - No saved vehicle selected → "Continue to Payment" (or "Continue"
  //     when payments are disabled). Selection is optional; users may
  //     always continue without picking one.
  //   - Saved vehicle selected but PIN not yet passed → "Continue"
  //     because the next action is the PIN sub-step, not payment.
  //   - Saved vehicle already PIN-verified → the payment flow follows,
  //     so the label reverts to the payment wording.
  const hasSelectedUnverifiedVehicle =
    !!state.selectedVehicleId && !state.vehicle?.verified;
  const paymentLabel = PAYMENTS_ENABLED ? 'Continue to Payment' : 'Continue';
  const registeredPlateRequired = ACCOUNT_CHOICE_ENABLED
    && (state.accountChoice === ACCOUNT_CHOICE.ACCOUNT
      || (openedAsRegistered && state.accountChoice == null))
    && !savedVehiclePickerVisible
    && !state.selectedVehicleId;
  // Same minimum the lookup and the server apply (>= 2 plate characters).
  const plateEntered = normalizePlateDisplay(licensePlate).length >= 2;
  const continueLabel = registeredPlateRequired
    ? 'Proceed'
    : (hasSelectedUnverifiedVehicle ? 'Continue' : paymentLabel);

  // Validation helpers for button disabled state
  const isStationMetaValid = state.stationMeta?.ratePerKwh && state.stationMeta.ratePerKwh > 0;
  const stationIdRegex = /^[A-Za-z0-9_/-]{1,128}$/;
  const isStationIdValid = state.stationId && stationIdRegex.test(state.stationId);
  const isValidForContinue = isStationMetaValid && isStationIdValid;

  const actions = [
    { id: 'back', label: 'Back', type: 'btn-outline-dark', disabled: isCreatingSession },
    { 
      id: 'continue', 
      label: isCreatingSession ? 'Creating Session...' : (plateCheck === 'checking' ? 'Checking…' : continueLabel),
      type: 'btn-dark',
      disabled: isCreatingSession || !isValidForContinue || plateCheck === 'checking'
        || (registeredPlateRequired && !plateEntered),
    },
  ];

  const handleActionClick = async (actionId) => {
    if (actionId === 'back') {
      // If we have a session, cancel it before going back
      console.log('[UserFlow] Cancelling session due to navigation from ChargeSetup');
      
      try {
        const result = await cancelNow();
        
        if (result.success) {
          console.log('[ChargeSetup]  Session cancelled successfully');
          // Silent cleanup - no toast notification
          // cancelSession() already resets state and navigates to step 1
        } else {
          // Handle "charging started" error specifically
          if (result.blockReason === 'charging_started') {
            console.error('[ChargeSetup]  Cannot cancel - charging started');
            showToast({
              message: result.error || 'Charging already started — please stop session instead.',
              type: 'error'
            });
          } else {
            console.error('[ChargeSetup]  Cancel failed:', result.error);
            showToast({
              message: result.error || 'Failed to cancel session',
              type: 'error'
            });
          }
        }
      } catch (error) {
        console.error('[ChargeSetup]  Cancel exception:', error);
        showToast({
          message: 'Failed to cancel session',
          type: 'error'
        });
      }
      return;
    }
    
    if (actionId === 'continue') await submitContinue();
  };

  // ONE submit path for the plate screen. Proceed (via ActionButtonWrapper),
  // the phone keyboard's Go/Done/Enter and a physical Enter (both via the
  // form's submit) all land here, and a single in-flight lock covers the
  // whole attempt -- plate lookup AND session creation -- so Enter followed
  // by a tap, or repeated Enters, can never start a second lookup/session.
  const submitContinue = async () => {
    track('charging.plate.submitted'); // the plate itself is never sent
    // Same gates as Proceed's disabled state, so Enter can't do what a tap can't.
    if (continueInFlight.current || isCreatingSession || !isValidForContinue) return;
    if (registeredPlateRequired && !plateEntered) {
      // Only reachable from the keyboard (Proceed is disabled): say why
      // nothing happened and keep the driver in the field.
      setPlateCheck('invalid');
      plateInputRef.current?.focus();
      return;
    }
    continueInFlight.current = true;
    try {
      await runContinue();
    } finally {
      continueInFlight.current = false;
    }
  };

  const runContinue = async () => {
    // Pre-flight validation logging
    console.log('[ChargeSetup] Pre-flight validation:', {
      stationId: state.stationId,
      connectorId: state.connectorId,
      stationMeta: state.stationMeta,
      hasStationMeta: !!state.stationMeta,
      hasRatePerKwh: !!state.stationMeta?.ratePerKwh,
      ratePerKwhValue: state.stationMeta?.ratePerKwh
    });
    
    // Validation 1: Check stationMeta is loaded
    if (!state.stationMeta) {
      console.error('[ChargeSetup] Validation failed: stationMeta not loaded');
      showToast({
        message: 'Charger information not loaded. Please go back and try again.',
        type: 'error'
      });
      return;
    }
    
    // Validation 2: Check pricing is defined (per-kWh or per-minute)
    const hasValidRate = state.stationMeta.billingUnit === 'minute'
      ? (state.stationMeta.ratePerMinute > 0)
      : (state.stationMeta.ratePerKwh > 0);
    if (!hasValidRate) {
      console.error('[ChargeSetup] Validation failed: invalid rate', { billingUnit: state.stationMeta.billingUnit, ratePerKwh: state.stationMeta.ratePerKwh, ratePerMinute: state.stationMeta.ratePerMinute });
      showToast({
        message: 'Charger pricing information is missing. Please go back and try again.',
        type: 'error'
      });
      return;
    }
    
    // Validation 3: Check stationId format
    const stationIdRegex = /^[A-Za-z0-9_/-]{1,128}$/;
    if (!state.stationId || !stationIdRegex.test(state.stationId)) {
      console.error('[ChargeSetup] Validation failed: invalid stationId format', state.stationId);
      showToast({
        message: 'Invalid charger ID. Please scan the QR code again or contact support.',
        type: 'error'
      });
      return;
    }
    
    console.log('[ChargeSetup]  Pre-flight validation passed');

    // Registered path: the plate must be a registered vehicle before any
    // session exists. One check at a time; repeated taps are ignored.
    if (registeredPlateRequired) {
      setPlateCheck('checking');
      let recognised = false;
      try {
        ({ recognised } = await checkRegisteredPlate(normalizePlateDisplay(licensePlate)));
      } catch {
        setPlateCheck('unavailable');
        return;
      }
      if (!recognised) {
        setPlateCheck('not_recognised');
        return;
      }
      setPlateCheck('idle');
      // The field is about to leave the screen: let the keyboard go too.
      plateInputRef.current?.blur();
    }
    
    // Create server session BEFORE moving to next step
    // This ensures we have session_uid for payment and charging
    if (!state.sessionUid) {
      setIsCreatingSession(true);
      
      try {
        // Build pricing snapshot from station metadata
        const pricingSnapshot = {
          rate_per_kwh: state.stationMeta.ratePerKwh,
          rate_per_minute: state.stationMeta.ratePerMinute || null,
          billing_unit: state.stationMeta.billingUnit || 'kwh',
          session_fee: state.stationMeta.sessionFee || 0,
          currency: state.currency || 'BBD',
          min_charge_amount: state.stationMeta.minChargeAmount || 0,
        };
        
        console.log('[ChargeSetup] Creating session:', {
          stationId: state.stationId,
          connectorId: state.connectorId,
          pricingSnapshot
        });
        
        // Plate attribution. Priority order matches what the driver
        // actually did in this step:
        //   1. They tapped a saved vehicle card → send vehicleId so
        //      the backend snapshots from the authoritative vehicle
        //      row (source: saved_vehicle). Any typed plate is
        //      ignored server-side to prevent typos from overwriting
        //      the picked plate.
        //   2. They typed a plate manually → send licensePlate,
        //      source manual_entry. Backend normalises + stores as
        //      the historical snapshot.
        //   3. Neither → send nothing; the session row carries NULL
        //      plate columns. This is legitimate for guests who
        //      never entered a plate.
        const selectedVehicleId = state.selectedVehicleId || state.vehicle?.vehicleId || null;
        const typedPlate = state.vehicle?.licensePlateRaw || state.licensePlate || null;
        const plateCaptureSource = selectedVehicleId
          ? 'saved_vehicle'
          : (typedPlate ? 'manual_entry' : null);

        const result = await createCustomerSession({
          stationId: state.stationId,
          // connectorId is REQUIRED and must be a real physical
          // connector id selected by the driver (or auto-selected
          // for single-connector chargers) in EvProfile. No fallback
          // to 1 — see createCustomerSession's INVALID_CONNECTOR_ID
          // guard for the failure mode when this is null.
          connectorId: state.connectorId,
          pricingSnapshot,
          // Atomically redeem the code inside the session-create tx.
          // If it's been used/expired since we validated, the server
          // returns REDEEM_CODE_INVALID and no session is created.
          redeemCode: appliedCredit ? appliedCredit.code : null,
          vehicleId: selectedVehicleId,
          licensePlate: selectedVehicleId ? null : typedPlate,
          plateCaptureSource,
        });

        if (result.sessionUid) {
          console.log('[ChargeSetup]  Session created:', {
            sessionUid: result.sessionUid, 
            status: result.session?.status 
          });
          
          // Store session_uid in localStorage
          localStorage.setItem('activeSessionUid', result.sessionUid);
          
          // Calculate session budget in cents
          // For payment flows: will be set when payment is authorized (authorizedAmount)
          // For non-payment/simulator flows: use minimum charge as default budget
          // ROOT CAUSE FIX: Ensure sessionBudgetCents never 0 - fallback to $10.00 minimum
          let sessionBudgetCents;
          if (state.authorizedAmount && state.authorizedAmount > 0) {
            // Payment flow: use authorized amount
            sessionBudgetCents = state.authorizedAmount;
          } else {
            // Simulator flow: use min_charge_amount with $10.00 fallback
            const minChargeAmountCents = Math.round((pricingSnapshot.min_charge_amount || 0) * 100);
            sessionBudgetCents = minChargeAmountCents > 0 ? minChargeAmountCents : 1000; // Default $10.00
          }
          
          console.log('[ChargeSetup]  Session budget set:', {
            sessionBudgetCents,
            sessionBudgetDollars: (sessionBudgetCents / 100).toFixed(2),
            hasAuthorizedAmount: !!state.authorizedAmount,
            minChargeAmount: pricingSnapshot.min_charge_amount,
            usedFallback: sessionBudgetCents === 1000 && !state.authorizedAmount
          });
          
          // Store session_uid AND pricingSnapshot in context
          // pricingSnapshot is locked-in at session creation for ETA calculations
          update({
            sessionUid: result.sessionUid,
            sessionId: result.sessionUid, // Also update legacy field for compatibility
            sessionStatus: result.session?.status,
            // The server tariff when the API applied one (P4b enforce) —
            // display that, never the snapshot this app sent.
            pricingSnapshot: result.pricingSnapshot || pricingSnapshot, // Store for ETA calculations
            authorization: result.authorization ?? null,
            sessionBudgetCents: sessionBudgetCents, // Store session budget for ETA
            // appliedCredit already lives in session state (written by
            // AmountPicker); no need to overwrite it here. The
            // credit_preview from the server is echoed back for
            // debugging only — the server has NOT yet redeemed the
            // code; it will do that inside the payment bootstrap.
          });
          
          // Saved-vehicle re-entry: reached only when the user has
          // ALREADY completed PIN verification on a prior pass
          // through /vehicles/verify and then navigated back to
          // ChargeSetup. `verified: true` is now only ever written
          // by VehicleVerification on a successful backend response,
          // so this branch cannot be used to bypass the PIN prompt
          // — clicking a saved-vehicle card sends the user straight
          // to VEHICLE_VERIFICATION (see selectSavedVehicle).
          if (state.selectedVehicleId && state.vehicle?.verified) {
            console.log('[ChargeSetup] Saved vehicle already PIN-verified — advancing');
            handleIncreaseStep();
            return;
          }

          // Check if license plate is entered
          const trimmedPlate = licensePlate.trim();
          if (trimmedPlate) {
            console.log('[ChargeSetup] License plate entered, navigating to PIN verification');
            // Store license plate in session state before navigating
            update({
              licensePlate: trimmedPlate,
              vehicle: {
                ...state.vehicle,
                licensePlateRaw: trimmedPlate,
                verified: false
              }
            });
            // Navigate to PIN verification step (will show PIN entry)
            setStep(STEPS.VEHICLE_VERIFICATION);
            return;
          }

          // No license plate - proceed with normal flow (no vehicle verification)
          console.log('[ChargeSetup] No license plate - proceeding to normal flow');
          handleIncreaseStep();
        } else {
          // Administrative maintenance override — the admin flipped
          // the flag on between EvProfile mount and this Start tap.
          // Surface the operator's copy verbatim and stop the flow;
          // the user can walk away or hit Find-another-charger from
          // the toast (the wizard's earlier steps already have the
          // maintenance-blocked screen for a fresh entry). We do
          // NOT retry — every subsequent attempt would just get
          // rejected until the admin returns the charger to service.
          if (result.error === 'station_under_maintenance') {
            console.warn('[ChargeSetup] Station flipped to maintenance mid-flow:', result.message);
            showToast({
              message: result.message || 'This charger is currently under maintenance. Please use another charger nearby.',
              type: 'error',
              duration: 6000,
            });
            return;
          }

          // Redeem-code failure: server rejected the code at session-
          // start (e.g. it was redeemed by another attempt in the
          // meantime). Clear the applied credit so the user can
          // retry without the code and give a clear message.
          if (result.error === 'REDEEM_CODE_INVALID') {
            console.warn('[ChargeSetup] Redeem code rejected at session-start:', result.message);
            // Clear the applied credit from session state; the user
            // must go back to the Pre-Auth screen to try a different
            // code (or continue without one).
            update({ appliedCredit: null });
            showToast({
              message: result.message || 'Redeem code could not be applied.',
              type: 'error',
            });
            return;
          }

          // P4b: the session-start authorizer refused this start
          // (AUTHZ_MODE=enforce). The message is driver copy per reason
          // code (never a client name). A plate-attributed start that can
          // still be paid by card says how: the plate here is optional.
          if (result.error === 'AUTHORIZATION_DENIED') {
            console.warn('[ChargeSetup] Session start not authorized:', result.reasonCode);
            // Platform cleanup, Phase 3: a refusal by the charger's access
            // settings carries the access outcome. Show it on the result
            // step (the backend's wording and actions); the toast below is
            // only for an API that sends no accessOutcome.
            if (isAccessOutcome(result.accessOutcome)) {
              // Nothing was created, so there is no attempt carrying the vehicle.
              update({ accessOutcome: result.accessOutcome, accessOutcomeSessionHasVehicle: false });
              setStep(STEPS.FLEET_ACCESS_RESULT);
              return;
            }
            const hasPlate = Boolean(selectedVehicleId || typedPlate);
            showToast({
              message: result.offerCard && hasPlate
                ? `${result.message} Clear the vehicle plate to continue with card payment.`
                : result.message,
              type: 'error',
              duration: 8000,
            });
            return;
          }

          // Check for CONNECTOR_BUSY error - trigger auto-recovery
          if (result.error === 'CONNECTOR_BUSY') {
            console.error('[ChargeSetup]  Connector busy, initiating auto-recovery');
            
            const recoveryResult = await handleConnectorBusy(result);
            
            if (recoveryResult.success) {
              // Auto-recovery succeeded, calculate and store sessionBudgetCents
              // ROOT CAUSE FIX: Ensure sessionBudgetCents never 0 - fallback to $10.00 minimum
              let sessionBudgetCents;
              if (state.authorizedAmount && state.authorizedAmount > 0) {
                sessionBudgetCents = state.authorizedAmount;
              } else {
                const minChargeAmountCents = Math.round((pricingSnapshot.min_charge_amount || 0) * 100);
                sessionBudgetCents = minChargeAmountCents > 0 ? minChargeAmountCents : 1000; // Default $10.00
              }
              
              update({ sessionBudgetCents });
              
              console.log('[ChargeSetup]  Auto-recovery successful, proceeding');
              handleIncreaseStep();
              return;
            } else if (recoveryResult.startedPolling) {
              // Polling started, keep user on this screen
              console.log('[ChargeSetup]  Polling started, waiting for recovery');
              return;
            } else {
              // Recovery failed with other error
              showToast({
                message: recoveryResult.error || 'Failed to create session. Please try again.',
                type: 'error'
              });
              return;
            }
          }
          
          console.error('[ChargeSetup]  Failed to create session:', result.message || result.error);
          showToast({
            message: result.message || 'Failed to create session. Please try again.',
            type: 'error'
          });
        }
      } catch (error) {
        console.error('[ChargeSetup]  Failed to create session:', {
          error: error.response?.data,
          status: error.response?.status,
          message: error.message
        });
        
        const errorMessage = error.response?.data?.message || error.message || 'Failed to create session';
        showToast({
          message: `Failed to create session: ${errorMessage}`,
          type: 'error'
        });
      } finally {
        setIsCreatingSession(false);
      }
    } else {
      // Session already exists (a refresh or Back from the PIN step reuses
      // it). Route exactly as the create path above does: advancing here
      // without verifying skipped the PIN step, so a FREE / fleet vehicle
      // was treated as a card payer and sent to the pre-authorization hold.
      console.log('[ChargeSetup] Session already exists:', state.sessionUid);

      // Already PIN-verified in this flow (only VehicleVerification writes
      // `verified: true`, and editing the plate resets it): unchanged.
      if (state.vehicle?.verified) {
        handleIncreaseStep();
        return;
      }

      // A vehicle to verify — a typed plate or a saved vehicle whose PIN has
      // not been entered yet. The PIN step keeps its own rules and its
      // "pay by card instead" way out.
      const trimmedPlate = licensePlate.trim();
      if (trimmedPlate || state.selectedVehicleId) {
        if (trimmedPlate) {
          update({
            licensePlate: trimmedPlate,
            vehicle: { ...state.vehicle, licensePlateRaw: trimmedPlate, verified: false },
          });
        }
        setStep(STEPS.VEHICLE_VERIFICATION);
        return;
      }

      // No vehicle at all: the card flow, as before.
      handleIncreaseStep();
    }
  };

  return (
    <div className="ev-content-wrapper">
      <EvHeader
        headerAction={{
          label: headerLabel,
          action: null,
        }}
      />

      <div
        ref={bodyRef}
        className="ev-inner-profile-wrapper charge-setup-body"
        style={keyboardInset ? { paddingBottom: keyboardInset } : undefined}
      >
        <h2 className="heading">{heading}</h2>
        <p className="text-muted">{bodyText}</p>
        
        {/*
          Signed-in "Choose a vehicle" section. Rendered ONLY when
          it's useful (auth resolved, user signed in, at least one
          vehicle, not opted-out into manual entry). By design this
          replaces — never sits alongside — the manual plate form
          below, so the user never sees both inputs at the same time.
        */}
        {savedVehiclePickerVisible ? (
          <SavedVehiclePicker
            vehicles={savedVehicles}
            selectedVehicleId={state.selectedVehicleId}
            onSelect={selectSavedVehicle}
            onUseAnother={useAnotherVehicleFlow}
            disabled={isCreatingSession}
          />
        ) : (
          <>
            {/*
              Reversible mode switch. Only rendered when the user has
              saved vehicles AND is currently in manual mode — guests
              and zero-vehicle users never see this action, and it
              never sits alongside the picker.
            */}
            {isAuthenticated && savedVehicleCount > 0 && useAnotherVehicle && (
              <button
                type="button"
                className="charge-setup-back-to-saved"
                onClick={backToSavedVehiclesFlow}
                disabled={isCreatingSession}
              >
                <LuArrowLeft aria-hidden="true" />
                <span>Back to saved vehicles</span>
              </button>
            )}

            {/* License Plate Input (Optional) — unchanged from the
                pre-redeem-code baseline. The redeem-code UI lives on the
                Pre-Auth Hold screen (AmountPicker) now. */}
            {/* noValidate: an empty required plate gets OUR message on every
                browser, not a native bubble that differs per OS. */}
            <form
              className="license-plate-section"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                submitContinue();
              }}
            >
            <label htmlFor="license-plate" className="license-plate-label">
              {registeredPlateRequired ? 'License Plate' : 'License Plate (optional)'}
            </label>
            <input
              ref={plateInputRef}
              id="license-plate"
              name="license-plate"
              type="text"
              inputMode="text"
              value={licensePlate}
              onChange={handleLicensePlateChange}
              className="license-plate-input"
              placeholder="e.g., B1234"
              // readOnly, not disabled, while checking: disabling the focused
              // field closes the phone keyboard, so a "not recognized" answer
              // would leave the driver to tap back in before correcting it.
              disabled={isCreatingSession}
              readOnly={plateCheck === 'checking'}
              aria-busy={plateCheck === 'checking' || undefined}
              enterKeyHint="go"
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              required={registeredPlateRequired}
              aria-invalid={plateCheck === 'invalid' || plateCheck === 'not_recognised' || undefined}
              aria-describedby={plateCheck === 'invalid' ? 'license-plate-invalid' : undefined}
            />
            <p className="license-plate-helper">
              {registeredPlateRequired
                ? 'Enter the license plate of your registered vehicle.'
                : 'Enter your plate if your vehicle is registered for fleet or special rates.'}
            </p>
            {plateCheck === 'invalid' && (
              <div ref={plateCheckRef} className="license-plate-check" role="alert" id="license-plate-invalid" data-testid="plate-invalid">
                <p className="license-plate-check__body">Enter your license plate.</p>
              </div>
            )}
            {plateCheck === 'not_recognised' && (
              <div ref={plateCheckRef} className="license-plate-check" role="alert" data-testid="plate-not-recognised">
                <p className="license-plate-check__title">License plate not recognized.</p>
                <p className="license-plate-check__body">Check the plate and try again, or continue as a guest.</p>
                <button type="button" className="btn btn-outline-dark license-plate-check__guest" onClick={continueAsGuest}>
                  Continue as guest
                </button>
              </div>
            )}
            {plateCheck === 'unavailable' && (
              <div ref={plateCheckRef} className="license-plate-check" role="alert" data-testid="plate-check-unavailable">
                <p className="license-plate-check__body">We couldn&rsquo;t check the plate right now. Please try again.</p>
              </div>
            )}
          </form>

          </>
        )}

        {/* Compact read-only credit summary — shown only if the user
            already applied a code on the previous step. */}
        {appliedCredit && (
          <div className="redeem-code-applied-inline" role="status">
            <span className="redeem-code-applied-title">Credit code applied</span>
            <span className="redeem-code-applied-code">{appliedCredit.code}</span>
            <span className="redeem-code-applied-amount">
              −{Number(appliedCredit.amount).toFixed(2)} {appliedCredit.currency}
            </span>
          </div>
        )}

        {connectorBusyError && (
          <Notice
            className="connector-busy-notice"
            variant="warning"
            actions={
              <>
                {connectorBusyError.canClear && (
                  <button
                    className="connector-busy-refresh"
                    onClick={handleClearBlockingSession}
                    disabled={isPolling}
                  >
                    Clear Previous Attempt
                  </button>
                )}
                {connectorBusyError.canRetry && (
                  <button
                    className="connector-busy-refresh"
                    onClick={refetchStationStatus}
                    disabled={isPolling}
                  >
                    Refresh Status
                  </button>
                )}
              </>
            }
          >
            <p>{connectorBusyError.message}</p>
            {isPolling && (
              <p className="connector-busy-submessage">Checking status every 3 seconds…</p>
            )}
          </Notice>
        )}
      </div>

      <ActionButtonWrapper
        ref={ref}
        actions={actions}
        onClickAction={handleActionClick}
      />
    </div>
  );
};

export default ChargeSetup;
